import type { FC } from 'react';
import * as React from 'react';
import { Card, Stack, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { FormikProps } from 'formik';
import { getIn } from 'formik';
import {
  Button,
  CountrySelector,
  FTextInput,
  IbanInput,
} from '@pflegenavi/web-components';
import { DirectDebitConfirmRedirectWarning } from './DirectDebitConfirmRedirectWarning';
import { PaymentInitiationInfo } from '../PaymentInitiationInfo';
import type { ISetupMangopayDirectDebitValues } from './SetupMangopayDirectDebit';

export interface SetupMangopayDirectDebitViewProps {
  formik: FormikProps<ISetupMangopayDirectDebitValues>;
  disabled: boolean;
  countryOptions: Array<{ label: string; value: string }>;
  onChangeCountry: (newCountryId: string) => void;
  onCountryBlur: () => void;
  redirectWarningOpen: boolean;
  setRedirectWarningOpen: (open: boolean) => void;
  PaymentInitiationInfoComponent?: React.ComponentType;
}

export const SetupMangopayDirectDebitView: FC<
  SetupMangopayDirectDebitViewProps
> = ({
  formik,
  disabled,
  countryOptions,
  onChangeCountry,
  onCountryBlur,
  redirectWarningOpen,
  setRedirectWarningOpen,
  PaymentInitiationInfoComponent = PaymentInitiationInfo,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <RootContainer>
        <Stack direction="column" gap={3}>
          <Stack gap={1}>
            <Typography variant="h5">
              {t('mobile.family-member.direct-debit-form-title')}
            </Typography>
            <PaymentInitiationInfoComponent />
          </Stack>
          {/* Bank account owner selection removed as it's always 'resident' now */}
          <Stack direction="row" gap={2}>
            <FTextInput
              id="ownerName"
              name="ownerName"
              label={t('mangopay.direct-debit.setup.owner-name')}
              placeholder={t('mangopay.direct-debit.setup.owner-name')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              formik={formik}
              disabled={disabled}
            />
          </Stack>
          <IbanInput
            value={formik.values.iban ?? ''}
            setValue={(newIban) => formik.setFieldValue('iban', newIban)}
            error={
              getIn(formik.touched, 'iban') && getIn(formik.errors, 'iban')
            }
            onBlur={() => formik.setFieldTouched('iban', true)}
            disabled={disabled}
          />
          <Stack direction="row" gap={2}>
            <FTextInput
              id="city"
              name="city"
              label={t('mangopay.direct-debit.setup.city')}
              placeholder={t('mangopay.direct-debit.setup.city')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              formik={formik}
              disabled={disabled}
            />
            {/* Region field removed as it's not necessary */}
          </Stack>
          <Stack direction="row" gap={2}>
            <FTextInput
              id="addressLine1"
              name="addressLine1"
              label={t('mangopay.direct-debit.setup.address-line-1')}
              placeholder={t('mangopay.direct-debit.setup.address-line-1')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              formik={formik}
              disabled={disabled}
            />
            <FTextInput
              id="addressLine2"
              name="addressLine2"
              label={t('mangopay.direct-debit.setup.address-line-2')}
              placeholder={t('mangopay.direct-debit.setup.address-line-2')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              formik={formik}
              disabled={disabled}
            />
          </Stack>
          <Stack direction="row" gap={2}>
            <CountrySelector
              name="country"
              label={t('mangopay.direct-debit.setup.country')}
              onChangeCountry={onChangeCountry}
              onBlur={onCountryBlur}
              countries={countryOptions}
              countryId={formik.values.country}
              errorMessage={
                getIn(formik.touched, 'country') &&
                getIn(formik.errors, 'country')
              }
              fullWidth
              disabled={disabled}
            />
            <FTextInput
              id="postalCode"
              name="postalCode"
              label={t('mangopay.direct-debit.setup.postal-code')}
              placeholder={t('mangopay.direct-debit.setup.postal-code')}
              fullWidth
              InputLabelProps={{ shrink: true }}
              formik={formik}
              disabled={disabled}
            />
          </Stack>
        </Stack>
        <Stack alignItems="flex-end" marginTop={3}>
          <Button
            type="submit"
            disabled={!formik.isValid || disabled || redirectWarningOpen}
            onClick={() => setRedirectWarningOpen(true)}
            id="activate-direct-debit-button"
            variant="contained"
          >
            {t('mangopay.direct-debit.setup.initiate')}
          </Button>
        </Stack>
      </RootContainer>

      <DirectDebitConfirmRedirectWarning
        open={redirectWarningOpen}
        setOpen={setRedirectWarningOpen}
        handleContinue={formik.submitForm}
        submitting={formik.isSubmitting}
      />
    </>
  );
};

const RootContainer = styled(Card)(({ theme }) =>
  theme.unstable_sx({
    p: 3,
    maxWidth: 800,
  })
);
